export type SharedCalendarListStatus = 'PENDING' | 'SUCCESS' | 'FAILURE';

export interface SharedCalendarItem {
  user_id: number;
  provider_calendar_id: string;
  name: string;
  is_primary: boolean;
  is_default: boolean;
}

export interface SharedCalendarPollResults {
  calendars: SharedCalendarItem[];
}

export interface SharedCalendarTriggerFetchResponse {
  data: { fetch_uuid: string };
  errors: string[];
}

export interface SharedCalendarListPollResponse {
  data: {
    status: SharedCalendarListStatus;
    results?: SharedCalendarPollResults;
  };
  errors: string[];
}

export interface SharedCalendarShowSettingResponse {
  data: { shared_calendar_enabled: boolean };
  errors: string[];
}

export interface UseGetSharedCalendarOptions {
  /** Passed to `list` as `type` (e.g. `saved`, `unsaved`). */
  listType?: string;
  /**
   * When `false`, `fetchSharedCalendars` does nothing and trigger/poll never run.
   * Drive from `GET /shared_calendars/show_setting` (`useSharedCalendarSetting`).
   * @default true
   */
  featureEnabled?: boolean;
}

const FETCH_ERROR_MESSAGES = {
  failure: 'Unable to load the available shared calendars. Try again later.',
  maxAttempts:
    'Unable to load the available shared calendars. Try again later.',
  missingUuid: 'Could not start shared calendar sync. Try again later.',
} as const;

/** Shown when the API returns `err_feature_not_enabled` (or similar) instead of raw error keys. */
export const SHARED_CALENDAR_FEATURE_DISABLED_MESSAGE =
  'Shared calendars are not enabled for this account. Ask your team to enable the feature, then try again.';

export type SharedCalendarFetchErrorReason =
  | 'api_errors'
  | 'failure_status'
  | 'max_attempts'
  | 'missing_fetch_uuid';

export class SharedCalendarFetchError extends Error {
  readonly reason: SharedCalendarFetchErrorReason;

  readonly apiErrors: string[];

  constructor(
    reason: SharedCalendarFetchErrorReason,
    message?: string,
    apiErrors: string[] = []
  ) {
    super(message ?? FETCH_ERROR_MESSAGES.failure);
    this.name = 'SharedCalendarFetchError';
    this.reason = reason;
    this.apiErrors = apiErrors;
  }
}

function errorStringsMentionFeatureDisabled(strings: unknown[]): boolean {
  return strings.some((s) =>
    String(s).toLowerCase().includes('feature_not_enabled')
  );
}

/** True when trigger/list failed because the shared-calendar product feature is off for the tenant. */
export function isSharedCalendarFeatureDisabledError(err: unknown): boolean {
  if (err instanceof SharedCalendarFetchError) {
    return errorStringsMentionFeatureDisabled(err.apiErrors);
  }
  const data = (err as { response?: { data?: { errors?: unknown[] } } })
    ?.response?.data;
  const errors = data?.errors;
  if (Array.isArray(errors)) {
    return errorStringsMentionFeatureDisabled(errors);
  }
  return false;
}

export function getToastMessageForFetchError(err: unknown): string {
  if (err instanceof SharedCalendarFetchError) {
    if (err.reason === 'missing_fetch_uuid') {
      return FETCH_ERROR_MESSAGES.missingUuid;
    }
    if (err.reason === 'max_attempts') {
      return FETCH_ERROR_MESSAGES.maxAttempts;
    }
    if (err.reason === 'failure_status') {
      return FETCH_ERROR_MESSAGES.failure;
    }
    if (err.apiErrors.length > 0) {
      if (errorStringsMentionFeatureDisabled(err.apiErrors)) {
        return SHARED_CALENDAR_FEATURE_DISABLED_MESSAGE;
      }
      return err.apiErrors.join(', ');
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return FETCH_ERROR_MESSAGES.failure;
}

/** Title for the trigger POST toast — never surfaces raw keys like `err_feature_not_enabled`. */
export function getSharedCalendarTriggerToastTitle(err: unknown): string {
  if (isSharedCalendarFeatureDisabledError(err)) {
    return SHARED_CALENDAR_FEATURE_DISABLED_MESSAGE;
  }
  return getToastMessageForFetchError(err);
}
